import React, { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { FormLabelAsterisk } from "@/components/ui/form"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { SearchableDropdown } from "@/components/ui/searchable-dropdown"
import { Badge } from "@/components/ui/badge"
import { X, Plus } from "lucide-react"
import { useLanguage } from '@/contexts/language-context'
import { useInternalProfiles } from '@/hooks/use-internal-profiles'
import { TimesheetAndExpenseFormData } from '@/lib/schemas'
import { TIMESHEET_APPROVAL_TYPE } from '@/lib/constants/work-order-codes'
import type { WorkerProfileSummary } from '@/types/api/engagement'

export interface ApproversProps {
  form: UseFormReturn<TimesheetAndExpenseFormData>
  isReadonly?: boolean
  selectedInternalCompany?: number
  onApproverAdd: (userProfileId: number) => void
  onApproverRemove: (index: number) => void
  fieldName?: 'approvers' | 'expenseApprovers'
  label?: string
  showSequenceForSingle?: boolean
  /** Server-resolved fallback names so an already-assigned approver still renders for a restricted caller. */
  profileSummaries?: Record<number, WorkerProfileSummary>
}

export const Approvers = ({
  form,
  isReadonly = false,
  selectedInternalCompany = 0,
  onApproverAdd,
  onApproverRemove,
  fieldName = 'approvers',
  label,
  profileSummaries,
}: ApproversProps) => {
  const { dictionary } = useLanguage()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  
  // Get internal user profiles for the selected internal company
  const { 
    internalProfiles: internalUserProfiles,
    loading: loadingInternalUserProfiles 
  } = useInternalProfiles({ organizationId: selectedInternalCompany })
  
  // Watch approvers from form (using dynamic field name)
  const approvers = form.watch(fieldName) || []
  const selectedApprovalSequence = form.watch('selectedApprovalSequence')

  // Determine when to show sequence badges
  // For timesheet approvers: show when multiple AND sequential
  // For expense approvers: never show (single approver only)
  const showSequenceBadges = fieldName === 'expenseApprovers' 
    ? false
    : approvers.length > 1 && selectedApprovalSequence === TIMESHEET_APPROVAL_TYPE.SEQUENTIAL

  // Get available approvers (excluding already selected ones)
  const getAvailableApprovers = () => {
    const selectedIds = approvers.map(a => a.userProfileId)
    return internalUserProfiles.filter(profile => !selectedIds.includes(profile.Id))
  }

  // Get the error for this field
  const fieldError = form.formState.errors[fieldName]

  // Determine the label to display
  const displayLabel = label || 
    (fieldName === 'expenseApprovers' 
      ? (dictionary?.engagement?.expense?.internalApprovers || "Expense Approvers")
      : (dictionary?.engagement?.timesheet?.timesheetApprovers || "Timesheet Approvers"))

  return (
    <fieldset className="space-y-3">
      <legend className="text-sm font-medium">
        {displayLabel}
        <FormLabelAsterisk show={Boolean(fieldError)} />
      </legend>

      {/* Approver List - only render container when there are approvers */}
      {approvers.length > 0 && (
        <div className="space-y-2">
          {approvers.map((approver, index) => {
            const selectedProfile = internalUserProfiles.find(p => p.Id === approver.userProfileId)
            // Fall back to the server-resolved name when the RLS-gated list doesn't include this
            // approver (restricted caller) so the assigned approver still renders read-only.
            // Resolved → "Name - Id"; set-but-unresolvable → "Profile ID {id}" (matches the History
            // view); genuinely empty row → the "Select approver" placeholder.
            const fallbackSummary = approver.userProfileId ? profileSummaries?.[approver.userProfileId] : undefined
            const approverDisplay = selectedProfile?.DisplayValue
              || (fallbackSummary?.fullName
                ? `${fallbackSummary.fullName} - ${approver.userProfileId}`
                : approver.userProfileId
                  ? `Profile ID ${approver.userProfileId}`
                  : 'Select approver')
            return (
              <Card key={index} className="flex items-center justify-between p-3">
                <div className="flex items-center gap-3">
                  {showSequenceBadges && (
                    <Badge variant="default" className="w-6 h-6 rounded-full flex items-center justify-center p-0 text-xs bg-muted-foreground/20 text-foreground hover:bg-muted-foreground/30">
                      {index + 1}
                    </Badge>
                  )}
                  <div className="font-medium text-sm">
                    {approverDisplay}
                  </div>
                </div>
                {!isReadonly && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => onApproverRemove(index)}
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </Card>
            )
          })}
        </div>
      )}

      {/* Add Approver Button with Dropdown */}
      {!isReadonly && (
        <Popover open={dropdownOpen && selectedInternalCompany !== 0 && !(fieldName === 'expenseApprovers' && approvers.length >= 1)} onOpenChange={setDropdownOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={selectedInternalCompany === 0 || (fieldName === 'expenseApprovers' && approvers.length >= 1)}
              className="h-9 px-3 text-sm w-full justify-start"
            >
              <Plus className="h-4 w-4 mr-2" />
              {selectedInternalCompany === 0 
                ? "Select internal company first" 
                : "Add approver"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-1" style={{ width: "var(--radix-popover-trigger-width)" }}>
            <SearchableDropdown
              options={getAvailableApprovers()}
              value=""
              onValueChange={(value) => {
                if (value) {
                  onApproverAdd(parseInt(value))
                  setDropdownOpen(false)
                }
              }}
              getItemLabel={(profile) => profile.DisplayValue}
              getItemValue={(profile) => profile.Id.toString()}
              searchPlaceholder="Search profiles..."
              emptyMessage={loadingInternalUserProfiles ? "Loading profiles..." : undefined}
            />
          </PopoverContent>
        </Popover>
      )}

      {/* Show validation errors immediately since fieldset is never "touched" */}
      {fieldError && typeof fieldError === 'object' && 'message' in fieldError && (
        <div className="border border-destructive/20 rounded-md p-4 bg-destructive/5">
          <div className="flex items-center gap-2 text-destructive">
            <span className="text-sm font-medium">
              {fieldError.message as string}
            </span>
          </div>
        </div>
      )}
    </fieldset>
  )
}

export default Approvers

